import { Navigate } from 'react-router-dom';
import { useEffect, useRef, useState } from 'react';
import { useAuth } from '@/context/AuthContext';

interface PrivateRouteProps {
  children: React.ReactNode;
  requireAdmin?: boolean;
  requireSuperAdmin?: boolean;
}

export default function PrivateRoute({ children, requireAdmin, requireSuperAdmin }: PrivateRouteProps) {
  const { user, profile, loading, profileLoading } = useAuth();
  const [timedOut, setTimedOut] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const needsPermissionCheck = requireAdmin || requireSuperAdmin;

  // Timeout de 3s — se o perfil não carregar, deixa passar e verifica depois
  useEffect(() => {
    if (needsPermissionCheck && profileLoading && !profile) {
      timerRef.current = setTimeout(() => setTimedOut(true), 3000);
    } else {
      setTimedOut(false);
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    }
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [profileLoading, needsPermissionCheck, profile]);

  // 1. Aguardar sessão inicial (loading do auth)
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="flex flex-col items-center gap-3">
          <div className="w-10 h-10 flex items-center justify-center">
            <i className="ri-loader-4-line text-2xl text-slate-400 animate-spin" />
          </div>
          <p className="text-sm text-slate-500">A carregar...</p>
        </div>
      </div>
    );
  }

  // 2. Sem sessão → login
  if (!user) return <Navigate to="/login" replace />;

  // 3. Se já temos perfil, verificar permissões imediatamente (sem esperar)
  if (profile) {
    if (requireSuperAdmin && profile.role !== 'super_admin') {
      return <Navigate to="/" replace />;
    }
    if (requireAdmin && profile.role !== 'admin' && profile.role !== 'super_admin') {
      return <Navigate to="/" replace />;
    }
    return <>{children}</>;
  }

  // 4. Perfil ainda a carregar E precisamos de permissões E ainda não deu timeout
  if (needsPermissionCheck && profileLoading && !timedOut) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="flex flex-col items-center gap-3">
          <div className="w-10 h-10 flex items-center justify-center">
            <i className="ri-loader-4-line text-2xl text-slate-400 animate-spin" />
          </div>
          <p className="text-sm text-slate-500">A verificar permissões...</p>
        </div>
      </div>
    );
  }

  // 5. Timeout ou sem perfil e sem permissões necessárias → deixa passar
  return <>{children}</>;
}
